import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/Modal';
import { Button } from '@/components/Button';
import { Select } from '@/components/Select';
import { publishToGoogleAds } from '@/api/apps';
import type { App } from '@/api/types';
import { formatDateTime } from '@/lib/datetime';

type StatusFilter = '' | 'active' | 'paused' | 'banned';
type PlatformFilter = '' | 'android' | 'ios';
type AdvertisedFilter = '' | 'yes' | 'no';
type InWorkFilter = '' | 'on' | 'off';

// Per-app outcome of one run. `rejected` is the upstream's 200 + ok=false
// (Google refused the campaign); `error` is our side or transport failing.
type RowResult =
  | { state: 'running' }
  | { state: 'ok'; text: string }
  | { state: 'rejected'; text: string }
  | { state: 'error'; text: string };

interface Props {
  isOpen: boolean;
  onClose: () => void;
  apps: App[];
}

// Pick apps with checkboxes and hand them, one by one, to the Google Ads
// Publish API. Sequential on purpose: the upstream creates a campaign per call
// and we want a readable per-row result rather than a burst of parallel jobs.
export function GoogleAdsPublishModal({ isOpen, onClose, apps }: Props) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<StatusFilter>('active');
  const [platform, setPlatform] = useState<PlatformFilter>('');
  const [advertised, setAdvertised] = useState<AdvertisedFilter>('no');
  const [inWork, setInWork] = useState<InWorkFilter>('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<Record<string, RowResult>>({});
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  // Closing the modal mid-run stops the loop after the in-flight request.
  const cancelled = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    cancelled.current = false;
    setSelected(new Set());
    setResults({});
    setProgress({ done: 0, total: 0 });
  }, [isOpen]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return apps.filter((a) => {
      if (q && !a.name.toLowerCase().includes(q) && !a.bundle_id.toLowerCase().includes(q)) return false;
      if (status === 'banned' && !a.is_banned) return false;
      if (status === 'active' && (a.is_banned || a.status !== 'active')) return false;
      if (status === 'paused' && (a.is_banned || a.status !== 'paused')) return false;
      if (platform && a.platform !== platform) return false;
      if (advertised === 'yes' && !a.advertised_at) return false;
      if (advertised === 'no' && a.advertised_at) return false;
      if (inWork === 'on' && !a.in_work) return false;
      if (inWork === 'off' && a.in_work) return false;
      return true;
    });
  }, [apps, search, status, platform, advertised, inWork]);

  const allVisibleSelected = filtered.length > 0 && filtered.every((a) => selected.has(a.id));
  const someVisibleSelected = filtered.some((a) => selected.has(a.id));

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) filtered.forEach((a) => next.delete(a.id));
      else filtered.forEach((a) => next.add(a.id));
      return next;
    });
  };

  const run = async () => {
    // Keep the table order so progress reads top to bottom.
    const targets = apps.filter((a) => selected.has(a.id));
    if (targets.length === 0) return;
    setRunning(true);
    setResults({});
    setProgress({ done: 0, total: targets.length });
    for (let i = 0; i < targets.length; i++) {
      if (cancelled.current) break;
      const app = targets[i];
      setResults((prev) => ({ ...prev, [app.id]: { state: 'running' } }));
      try {
        const r = await publishToGoogleAds(app.id);
        const row: RowResult = r.ok
          ? { state: 'ok', text: r.created?.length ? r.created.join(', ') : 'Campaign created' }
          : { state: 'rejected', text: (r.errors || []).join('; ') || 'Rejected by Google Ads' };
        setResults((prev) => ({ ...prev, [app.id]: row }));
      } catch (e) {
        setResults((prev) => ({ ...prev, [app.id]: { state: 'error', text: e instanceof Error ? e.message : 'Request failed' } }));
      }
      setProgress({ done: i + 1, total: targets.length });
    }
    setRunning(false);
    queryClient.invalidateQueries({ queryKey: ['apps'] });
  };

  const close = () => {
    cancelled.current = true;
    onClose();
  };

  const finished = !running && progress.total > 0 && progress.done === progress.total;
  const okCount = Object.values(results).filter((r) => r.state === 'ok').length;
  const badCount = Object.values(results).filter((r) => r.state === 'rejected' || r.state === 'error').length;

  const inputCls = 'block w-full px-3 py-1.5 text-sm border border-border-strong rounded-md bg-surface focus:outline-none focus:ring-primary focus:border-primary';

  return (
    <Modal isOpen={isOpen} onClose={close} title="Publish to Google Ads" size="3xl">
      <div className="space-y-4">
        <p className="text-sm text-content-subtle">
          Selected apps are handed to the Google Ads Publish API one by one; it pulls each listing through the Public API,
          creates an App campaign and stamps the app as advertised.
        </p>

        {/* Filters */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Name or package"
            aria-label="Search apps"
            className={`${inputCls} col-span-2 sm:col-span-1`}
          />
          <Select
            size="sm"
            value={status}
            onChange={(v) => setStatus(v as StatusFilter)}
            options={[
              { value: '', label: 'Any status' },
              { value: 'active', label: 'Active' },
              { value: 'paused', label: 'Paused' },
              { value: 'banned', label: 'Banned' },
            ]}
          />
          <Select
            size="sm"
            value={platform}
            onChange={(v) => setPlatform(v as PlatformFilter)}
            options={[
              { value: '', label: 'Any platform' },
              { value: 'android', label: 'Android' },
              { value: 'ios', label: 'iOS' },
            ]}
          />
          <Select
            size="sm"
            value={advertised}
            onChange={(v) => setAdvertised(v as AdvertisedFilter)}
            options={[
              { value: '', label: 'Advertised: any' },
              { value: 'no', label: 'Not advertised' },
              { value: 'yes', label: 'Advertised' },
            ]}
          />
          <Select
            size="sm"
            value={inWork}
            onChange={(v) => setInWork(v as InWorkFilter)}
            options={[
              { value: '', label: 'In work: any' },
              { value: 'on', label: 'In work' },
              { value: 'off', label: 'Not in work' },
            ]}
          />
        </div>

        {/* App list */}
        <div className="border border-border rounded-md overflow-hidden">
          <div className="flex items-center gap-3 px-3 py-2 bg-surface-muted text-xs text-content-muted border-b border-border">
            <input
              type="checkbox"
              checked={allVisibleSelected}
              ref={(el) => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
              onChange={toggleVisible}
              disabled={running || filtered.length === 0}
              aria-label="Select all shown apps"
              className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
            />
            <span className="flex-1">
              {filtered.length} shown · {selected.size} selected
            </span>
            {selected.size > 0 && !running && (
              <button type="button" onClick={() => setSelected(new Set())} className="text-content-subtle hover:text-content">
                Clear
              </button>
            )}
          </div>
          <div className="max-h-[50vh] overflow-y-auto divide-y divide-border">
            {filtered.length === 0 && (
              <div className="px-3 py-6 text-sm text-content-subtle text-center">No apps match the filters</div>
            )}
            {filtered.map((app) => {
              const res = results[app.id];
              return (
                <label
                  key={app.id}
                  className={`flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-surface-muted ${selected.has(app.id) ? 'bg-primary/5' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selected.has(app.id)}
                    onChange={() => toggleOne(app.id)}
                    disabled={running}
                    className="h-4 w-4 rounded border-border-strong text-primary focus:ring-primary"
                  />
                  <div className="w-8 h-8 rounded-md overflow-hidden border border-border bg-surface-muted shrink-0">
                    {app.icon_url && (
                      <img src={app.icon_url} alt="" width={32} height={32} className="w-full h-full object-cover" loading="lazy" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-medium text-content truncate">{app.name}</span>
                      <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium uppercase ${app.platform === 'ios' ? 'bg-surface-muted text-content-muted' : 'bg-success-soft text-success'}`}>
                        {app.platform}
                      </span>
                      {app.is_banned ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-danger-soft text-danger">Banned</span>
                      ) : app.status !== 'active' ? (
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-surface-muted text-content-muted">Paused</span>
                      ) : null}
                    </div>
                    <div className="text-xs text-content-subtle font-mono truncate">
                      {app.bundle_id}
                      {app.advertised_at && <span className="font-sans"> · advertised {formatDateTime(app.advertised_at)}</span>}
                    </div>
                    {res && res.state !== 'running' && (
                      <div className={`text-xs mt-0.5 ${res.state === 'ok' ? 'text-success' : 'text-danger'}`}>
                        {res.state === 'ok' ? '✓ ' : '✗ '}{res.text}
                      </div>
                    )}
                  </div>
                  {res?.state === 'running' && (
                    <svg className="w-4 h-4 animate-spin text-content-subtle shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16.023 9.348h4.992V4.356M2.985 19.644v-4.992h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                    </svg>
                  )}
                </label>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 pt-1">
          <div className="text-sm">
            {running && <span className="text-content-muted">Publishing {progress.done} / {progress.total}…</span>}
            {finished && (
              <span className={badCount ? 'text-danger' : 'text-success'}>
                Done: {okCount} created{badCount ? `, ${badCount} failed` : ''}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button type="button" variant="secondary" onClick={close}>
              {finished ? 'Close' : 'Cancel'}
            </Button>
            <Button type="button" onClick={run} loading={running} disabled={selected.size === 0}>
              Publish {selected.size > 0 ? selected.size : ''} to Google Ads
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
